/**
 * Phase 2 — Form field conditional visibility (single-clause).
 *
 * A field is visible when it has no `visible_if` rule, or its rule evaluates
 * truthy against the current form data. Mirrors the logic in
 * /app/backend/routes/forms.py so server-side and client-side stay in sync.
 *
 * Supported operators:
 *   - equals         (string/number, also select/radio)
 *   - not_equals
 *   - is_checked     (boolean checkbox driver)
 *   - is_not_checked
 *   - contains       (checkbox_group contains a value, OR text-substring)
 *
 * Unknown / malformed rules fail OPEN (visible) so a broken template never
 * silently hides the whole form.
 */

const VALUE_OPS = new Set(['equals', 'not_equals', 'contains']);

/** Build a {field_key: field} index from a list of fields. */
function _byKey(fields) {
  const idx = {};
  (fields || []).forEach(f => {
    if (f && f.field_key) idx[f.field_key] = f;
  });
  return idx;
}

function _valueEquals(current, target, parent) {
  if (current === null || current === undefined) return false;
  if (parent && parent.field_type === 'number') {
    const a = Number(current);
    const b = Number(target);
    if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  }
  return String(current) === String(target);
}

/**
 * Decide whether `field` is visible given `data` (a dict of field_key→value)
 * and the surrounding `fields` list (used to resolve parent type semantics).
 *
 * Returns boolean.
 */
export function evaluateVisibility(field, data, fields) {
  if (!field) return true;
  const cfg = field.config || field.config_json || {};
  const rule = cfg.visible_if;
  if (!rule || typeof rule !== 'object') return true;
  const { field_key: parentKey, operator: op } = rule;
  if (!parentKey || !op) return true;

  const fieldsByKey = Array.isArray(fields) ? _byKey(fields) : (fields || {});
  const parent = fieldsByKey[parentKey];
  if (!parent) return true;
  const current = data ? data[parentKey] : undefined;
  const target = rule.value;

  switch (op) {
    case 'is_checked': return !!current;
    case 'is_not_checked': return !current;
    case 'equals': return _valueEquals(current, target, parent);
    case 'not_equals': return !_valueEquals(current, target, parent);
    case 'contains': {
      if (Array.isArray(current)) return current.includes(target);
      if (typeof current === 'string') return current.includes(String(target ?? ''));
      return false;
    }
    default: return true;
  }
}

/** Convenience: return only the fields that are visible. */
export function filterVisibleFields(fields, data) {
  if (!Array.isArray(fields)) return [];
  const idx = _byKey(fields);
  return fields.filter(f => evaluateVisibility(f, data || {}, idx));
}

export const VISIBILITY_OPERATORS = ['equals', 'not_equals', 'is_checked', 'is_not_checked', 'contains'];
export const VISIBILITY_VALUE_OPS = VALUE_OPS;
export const VISIBILITY_OP_BY_PARENT_TYPE = {
  text:           ['equals', 'not_equals', 'contains'],
  textarea:       ['equals', 'not_equals', 'contains'],
  number:         ['equals', 'not_equals'],
  date:           ['equals', 'not_equals'],
  checkbox:       ['is_checked', 'is_not_checked'],
  select:         ['equals', 'not_equals'],
  radio:          ['equals', 'not_equals'],
  checkbox_group: ['contains'],
};
export const VISIBILITY_ELIGIBLE_PARENT_TYPES = new Set(Object.keys(VISIBILITY_OP_BY_PARENT_TYPE));

export const OPERATOR_LABELS = {
  equals: 'equals',
  not_equals: 'does not equal',
  is_checked: 'is checked',
  is_not_checked: 'is not checked',
  contains: 'contains',
};
